/**
 * © 2026 iTech India Private Limited. All rights reserved.
 * Licensed under the iTech End User License Agreement (EULA).
 * Unauthorized use or distribution is prohibited.
 */

import { zodResolver } from '@hookform/resolvers/zod';
import { CreateServiceItemSchema, type CreateServiceItemInput } from '@therapy/shared-validators';
import { type SyntheticEvent, useCallback, type JSX } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { FormField } from '../../components/form/form-field.js';
import { Button } from '../../components/ui/button.js';
import { Modal } from '../../components/ui/modal.js';
import { type ServiceItem, type ServiceItemPayload } from '../../lib/api/finance.js';
import { useCreateServiceItem, useUpdateServiceItem } from '../../lib/queries/finance.js';
import { rupeesToPaise } from '../../lib/utils/money.js';

interface ServiceItemFormProps {
  readonly open: boolean;
  readonly onOpenChange: (open: boolean) => void;
  /** Present when editing an existing item; absent when adding. */
  readonly item: ServiceItem | null;
  readonly onSaved: () => void;
}

/**
 * The price field collects rupees (converted to paise on submit), so the edit
 * default is rupees too; 0 leaves an effectively-empty field when adding.
 */
function defaultsFor(item: ServiceItem | null): CreateServiceItemInput {
  return {
    name: item?.name ?? '',
    description: item?.description ?? '',
    unitPricePaise: item !== null ? item.unitPricePaise / 100 : 0,
  };
}

function buildPayload(values: CreateServiceItemInput): ServiceItemPayload {
  return {
    name: values.name,
    unitPricePaise: values.unitPricePaise,
    ...(values.description !== undefined && values.description !== ''
      ? { description: values.description }
      : {}),
  };
}

/** Add / edit a catalogue item (P1-9). Validated by shared `CreateServiceItemSchema`. */
export function ServiceItemForm({
  open,
  onOpenChange,
  item,
  onSaved,
}: ServiceItemFormProps): JSX.Element {
  const { t } = useTranslation();
  const create = useCreateServiceItem();
  const update = useUpdateServiceItem();
  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
  } = useForm<CreateServiceItemInput>({
    resolver: zodResolver(CreateServiceItemSchema),
    defaultValues: defaultsFor(item),
  });

  const submit = useCallback(
    async (values: CreateServiceItemInput): Promise<void> => {
      const payload = buildPayload(values);
      await (item !== null
        ? update.mutateAsync({ id: item.id, payload })
        : create.mutateAsync(payload));
      onSaved();
    },
    [create, update, item, onSaved],
  );
  const onFormSubmit = useCallback(
    (event: SyntheticEvent<HTMLFormElement>): void => {
      void handleSubmit(submit)(event);
    },
    [handleSubmit, submit],
  );
  const pending = isSubmitting || create.isPending || update.isPending;
  const failed = create.isError || update.isError;

  return (
    <Modal
      open={open}
      onOpenChange={onOpenChange}
      title={item !== null ? t('finance.catalogue.editTitle') : t('finance.catalogue.addTitle')}
      description={t('finance.catalogue.subtitle')}
      closeLabel={t('common.cancel')}
    >
      <form onSubmit={onFormSubmit} className="space-y-4" noValidate>
        <FormField
          id="svc-name"
          type="text"
          label={t('finance.catalogue.name')}
          autoComplete="off"
          error={errors.name?.message}
          registration={register('name')}
        />
        <FormField
          id="svc-price"
          type="number"
          label={t('finance.catalogue.price')}
          placeholder={t('finance.catalogue.priceHint')}
          autoComplete="off"
          inputMode="numeric"
          error={errors.unitPricePaise?.message}
          registration={register('unitPricePaise', {
            setValueAs: (v: string) => rupeesToPaise(Number(v)),
          })}
        />
        <FormField
          id="svc-description"
          type="text"
          label={t('finance.catalogue.description')}
          autoComplete="off"
          error={errors.description?.message}
          registration={register('description')}
        />
        {failed && (
          <p role="alert" className="text-sm text-red-600">
            {t('common.saveError')}
          </p>
        )}
        <div className="flex justify-end gap-3 pt-1">
          <Button type="button" variant="outline" onClick={onSaved}>
            {t('common.cancel')}
          </Button>
          <Button type="submit" disabled={pending}>
            {t('finance.catalogue.save')}
          </Button>
        </div>
      </form>
    </Modal>
  );
}
